import ListItem from "@tiptap/extension-list-item";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/**
 * Extracts the first color value from a node's textStyle marks.
 * Traverses the node tree to find the first text with a color style.
 */
const extractColorFromNode = (node: any): string | null => {
  if (node.marks) {
    for (const mark of node.marks) {
      if (mark.type.name === "textStyle" && mark.attrs.style) {
        const styleStr = mark.attrs.style as string;
        const colorMatch = styleStr.match(/color:\s*([^;]+)/i);
        if (colorMatch) {
          return colorMatch[1].trim();
        }
      }
    }
  }
  if (node.content) {
    for (let i = 0; i < node.content.childCount; i++) {
      const color = extractColorFromNode(node.content.child(i));
      if (color) return color;
    }
  }
  return null;
};

/**
 * Updates the DOM <li> elements to have their color match the first text color.
 * This ensures ::marker inherits the correct color in real-time.
 */
const syncListItemColors = (view: any) => {
  const { doc } = view.state;

  doc.descendants((node: any, pos: number) => {
    if (node.type.name === "listItem") {
      const color = extractColorFromNode(node);
      const domNode = view.nodeDOM(pos);

      if (domNode && domNode.tagName === "LI") {
        const currentColor = domNode.style.color || "";
        const newColor = color || "";

        if (currentColor !== newColor) {
          if (newColor) {
            domNode.style.color = newColor;
          } else {
            domNode.style.removeProperty("color");
          }
        }
      }
    }
    return true;
  });
};

const listItemColorPluginKey = new PluginKey("listItemColorSync");

/**
 * Custom ListItem extension that propagates text color to the <li> element.
 *
 * TipTap applies color styles to <span> elements inside list items via textStyle marks.
 * The CSS ::marker pseudo-element inherits color from the <li> element, not from
 * descendant spans. This extension:
 * 1. Uses renderHTML to set the color on initial render/serialization
 * 2. Uses a ProseMirror plugin to sync colors in real-time during editing
 */
const CustomListItem = ListItem.extend({
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: listItemColorPluginKey,
        view() {
          return {
            update(view) {
              syncListItemColors(view);
            },
          };
        },
      }),
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const color = extractColorFromNode(node);
    const attrs = { ...HTMLAttributes };

    if (color) {
      const existingStyle = attrs.style || "";
      attrs.style = existingStyle
        ? `${existingStyle}; color: ${color}`
        : `color: ${color}`;
    }

    return ["li", attrs, 0];
  },
});

export default CustomListItem;
